import { useState, useEffect, useMemo, useRef } from 'react';
import { INITIAL_SEARCH_PARAMS, INITIAL_ALERT_INFO, getBasicColumns } from '../constants/invoiceConstants';

export const useInvoiceState = (t, cachedState) => {
    const initialSearchParams = useMemo(() => cachedState?.searchParams ?? INITIAL_SEARCH_PARAMS, []);
    const [searchParams, _setSearchParams] = useState(initialSearchParams);
    const searchParamsRef = useRef(initialSearchParams);
    const [page, setPage] = useState(() => cachedState?.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    /** 데스크톱 테이블 컬럼 정렬 — API 상세 조회 slice와 동기화 */
    const [invoiceTableSortConfig, setInvoiceTableSortConfig] = useState({
        key: null,
        direction: null
    });
    const [showExtendedView, setShowExtendedView] = useState(false);
    const [showMyOrderOnly, setShowMyOrderOnly] = useState(
        () => cachedState?.showMyOrderOnly ?? true
    );
    /** true: 인보이스 번호가 있는 행은 목록에서 숨김 */
    const [hideInvoicedRows, setHideInvoicedRows] = useState(false);
    // t가 리렌더마다 바뀌면 useEffect([t])로 컬럼이 초기화되어 깜빡임 발생 → 마운트 시 한 번만 초기화
    const [checkedColumns, setCheckedColumns] = useState(() => {
        const initialState = {};
        getBasicColumns(t).forEach((column) => {
            initialState[column.id] = column.defaultHidden !== true;
        });
        return initialState;
    });
    const [columnPanelOpen, setColumnPanelOpen] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [alertInfo, setAlertInfo] = useState(INITIAL_ALERT_INFO);
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [hasSearched, setHasSearched] = useState(() => cachedState?.hasSearched ?? false);

    const basicColumns = useMemo(() => getBasicColumns(t), [t]);

    useEffect(() => {
        setCheckedColumns((prev) => {
            let changed = false;
            const next = { ...prev };
            basicColumns.forEach((column) => {
                if (!(column.id in next)) {
                    next[column.id] = column.defaultHidden !== true;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [basicColumns]);

    // 컬럼 패널 외부 클릭 시 닫기 (설정 버튼 id 없으면 외부 클릭이 동작하지 않던 버그 수정)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!columnPanelOpen) return;
            const panel = document.getElementById('column-selection-panel');
            const settingsButton = document.getElementById('settings-button');
            if (panel?.contains(event.target)) return;
            if (settingsButton?.contains(event.target)) return;
            setColumnPanelOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [columnPanelOpen]);

    const setSearchParams = (updater) => {
        if (typeof updater === 'function') {
            _setSearchParams((prev) => {
                const next = updater(prev);
                searchParamsRef.current = next;
                return next;
            });
            return;
        }
        searchParamsRef.current = updater;
        _setSearchParams(updater);
    };

    const handleParamChange = (param, value) => {
        setSearchParams(prev => ({
            ...prev,
            [param]: value
        }));
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCheckColumn = (columnId) => {
        setCheckedColumns(prev => ({
            ...prev,
            [columnId]: !prev[columnId]
        }));
    };

    const toggleColumnPanel = () => {
        setColumnPanelOpen(prev => !prev);
    };

    const selectAllColumns = () => {
        const newState = {};
        getBasicColumns(t).forEach(column => {
            newState[column.id] = true;
        });
        setCheckedColumns(newState);
    };

    const unselectAllColumns = () => {
        const newState = {};
        getBasicColumns(t).forEach(column => {
            newState[column.id] = false;
        });
        setCheckedColumns(newState);
    };

    const handleSelectItem = (itemId, isSelected) => {
        if (isSelected) {
            setSelectedItems(prev => [...prev, itemId]);
        } else {
            setSelectedItems(prev => prev.filter(id => id !== itemId));
        }
    };

    const handleSelectAll = (isSelected, currentPageIds = null) => {
        if (isSelected) {
            const idsToSelect = currentPageIds || [];
            setSelectedItems(prev => {
                const newSelection = [...prev];
                idsToSelect.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
        } else {
            if (currentPageIds) {
                setSelectedItems(prev => prev.filter(id => !currentPageIds.includes(id)));
            } else {
                setSelectedItems([]);
            }
        }
    };

    const handleCloseAlert = () => {
        setAlertInfo(prev => ({
            ...prev,
            open: false,
            onConfirm: null,
            onClose: null
        }));
    };

    return {
        // 상태
        searchParams,
        searchParamsRef,
        page,
        rowsPerPage,
        invoiceTableSortConfig,
        setInvoiceTableSortConfig,
        showExtendedView,
        showMyOrderOnly,
        hideInvoicedRows,
        checkedColumns,
        columnPanelOpen,
        selectedItems,
        alertInfo,
        invoiceDialogOpen,
        hasSearched,


        // 상태 설정 함수들
        setSearchParams,
        setPage,
        setRowsPerPage,
        setShowExtendedView,
        setShowMyOrderOnly,
        setHideInvoicedRows,
        setCheckedColumns,
        setColumnPanelOpen,
        setSelectedItems,
        setAlertInfo,
        setInvoiceDialogOpen,
        setHasSearched,


        // 핸들러 함수들
        handleParamChange,
        handleChangePage,
        handleChangeRowsPerPage,
        handleCheckColumn,
        toggleColumnPanel,
        selectAllColumns,
        unselectAllColumns,
        handleSelectItem,
        handleSelectAll,
        handleCloseAlert
    };
}; 